<template>
  <div class="custom-ui-date-picker-wrapper">
    <div class="custom-ui-date-picker-label-row" v-if="label || info">
      <span class="custom-ui-date-picker-label" v-if="label" v-text="label" />
      <ui-field-info :text="info" />
    </div>
    <div class="custom-ui-date-picker" :class="[style, classes]">
      <i class="date-icon fa-regular fa-calendar"></i>
      <VueDatePicker
        :placeholder="placeholder"
        :disabled="disabled"
        :min-date="minDate"
        :max-date="maxDate"
        v-model="model"
        :config="{ noSwipe: false }"
        :action-row="actionRow"
        :locale="dateFnsLocale"
        :time-config="timeConfig"
        :input-attrs="inputAttrs"
        :range="range"
        :multi-calendars="range"
        @update:model-value="onInput"
        :formats="formats"
        :teleport="true"
      />
      <span class="custom-ui-date-picker-icon" v-if="hasValidation">
        <i class="success-icon fa-regular fa-circle-check" v-if="!anyError" />
        <i class="danger-icon fa-regular fa-circle-exclamation" v-else />
      </span>
    </div>
  </div>
</template>

<script setup lang="ts">
  import type { IUIDatePicker } from '@/types/Components'
  import { UseI18n } from '@/utils-ts'
  import { VueDatePicker } from '@vuepic/vue-datepicker'
  import '@vuepic/vue-datepicker/dist/main.css'
  import { de, enUS, fr, id, pl, pt, ru, th, tr, type Locale } from 'date-fns/locale'
  import { computed } from 'vue'

  const DATE_FNS_LOCALES: Record<string, Locale> = {
    en: enUS,
    tr,
    ru,
    pt,
    th,
    id,
    de,
    fr,
    pl,
  }

  const {
    label = '',
    disabled = false,
    placeholder = '',
    minDate = '',
    maxDate = '',
    timePicker = false,
    anyError = false,
    hasValidation = false,
    size = 'sm',
    clearable = false,
    range = false,
    info = '',
  } = defineProps<IUIDatePicker>()

  const { t, locale } = UseI18n()
  const emit = defineEmits(['change'])
  const model = defineModel<Date | string | Date[] | null>({ required: true, default: '' })

  const dateFnsLocale = computed(() => DATE_FNS_LOCALES[locale.value] ?? enUS)

  const actionRow = computed(() => ({
    cancelBtnLabel: t('admin_global_cancel'),
    selectBtnLabel: t('admin_global_select'),
  }))

  const timeConfig = computed(() => ({
    enableTimePicker: timePicker,
  }))

  const inputAttrs = computed(() => ({
    name: 'ui-date-picker',
    hideInputIcon: true,
    clearable: clearable,
  }))

  const style = computed(() => {
    if (hasValidation) {
      return anyError ? 'danger' : 'success'
    } else {
      return ''
    }
  })

  const classes = computed(() => {
    return [`size-${size}`, disabled ? 'disabled' : '', clearable ? 'clearable' : '']
  })

  const dateFormat = computed(() => {
    return timePicker ? 'dd/MM/yyyy HH:mm a' : 'dd/MM/yyyy'
  })

  const formats = computed(() => ({
    input: dateFormat.value,
  }))

  const onInput = () => {
    emit('change', model.value)
  }
</script>

<style lang="scss">
  .custom-ui-date-picker-wrapper {
    display: flex;
    flex-direction: column;
    overflow: visible;
    width: 100%;
    .custom-ui-date-picker-label-row {
      @include flexible($align: center, $wrap: wrap);
      gap: 0.35rem;
      margin-bottom: 0.5rem;
      .custom-ui-date-picker-label {
        @include column-label($margin: 0);
        width: auto;
      }
    }
    // Takvim menüsü teleport ile body'ye taşınır; oradaki tema src/assets/scss/classes.scss'te.
    // Burada yalnızca kendi kutumuzun içindeki input parçası biçimlenir (vue-datepicker v14, `dp--*` sınıfları).
    .custom-ui-date-picker {
      border: 1px solid $border-color;
      border-radius: 5px;
      display: flex;
      align-items: center;
      padding: calc(0.5em - 1px) 1em;
      background-color: $white;
      --dp-input-padding: 0;
      --dp-input-not-clearable-padding: 0;
      --dp-font-size: 1em; // .size-* sınıfının font-size'ını izler
      --dp-font-family: #{$font-family};
      --dp-text-color: #{$primary-text-color};

      .date-icon {
        color: $primary-text-color;
        margin-right: 0.5rem;
      }
      .dp--main {
        flex: 1;
        min-width: 0;
      }
      .dp--input {
        border: none;
        background: transparent;
        line-height: 1.5;
      }
      .dp--input-icons {
        padding: 0;
      }
      &.clearable .dp--input {
        padding-inline-end: 1.5rem; // temizle butonuna yer aç
      }
      &.disabled {
        cursor: not-allowed;
        border-color: $disabled;
        background-color: $cancel;
        .dp--input {
          cursor: not-allowed;
        }
      }
      &.danger {
        border: 1px solid $danger;
      }
      &.success {
        border: 1px solid $success;
      }
      .custom-ui-date-picker-icon {
        display: flex;
        justify-content: center;
        align-items: center;
        margin-left: 0.5rem;
        .danger-icon {
          color: $danger;
        }
        .success-icon {
          color: $success;
        }
      }
    }
  }
</style>
